import Skeleton from '@mui/material/Skeleton';
import type { SxProps, Theme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import type { WDSComponentProps } from '../../types';
import { useTrace } from '../../utils/trace';

type WDSComponentPropsNoSx = Omit<WDSComponentProps, 'sx'>;

export interface TableSkeletonProps extends WDSComponentPropsNoSx {
    rows: number;
    columns: number;
    actionSx?: SxProps<Theme>;
    hasAction?: boolean;
    rowSx?: SxProps<Theme>;
    skeletonSx?: SxProps<Theme>;
}

/**
 * A skeleton component to display a loading state for a table.
 *
 * It takes the number or rows and columns to display, and an optional action column.
 *
 * [Documentation](https://wds2docs.dev-sqnt.com/?path=/docs/components-tableskeleton)
 *
 * ### Example
 * ```tsx
 * <TableSkeleton
 *     rows={5}
 *     columns={5}
 *     hasAction
 * />
 * ```
 * @param param0
 * @returns
 */
export default function TableSkeleton({
    hasAction,
    rows,
    columns,
    actionSx,
    skeletonSx,
    rowSx,
    'automation-id': automationId,
}: TableSkeletonProps) {
    // Ensure integer numbers greater than zero are provided
    const rowCount = columns > 0 ? Number.parseInt(rows.toString(), 10) : 1;
    const columnCount = columns > 0 ? Number.parseInt(columns.toString(), 10) : 1;
    const getFullTrace = useTrace(automationId ?? 'table-skeleton');

    return (
        <>
            {[...Array(rowCount)].map((row, index) => (
                <TableRow
                    automation-id={getFullTrace(`row-${index}`)}
                    key={index}
                    sx={{ width: '100%', ...rowSx }}
                >
                    {[...Array(columnCount)].map((col, cellIndex) => (
                        <TableCell key={cellIndex}>
                            <Skeleton
                                animation="wave"
                                variant="text"
                                width="auto"
                                sx={{ height: '32px', ...skeletonSx }}
                            />
                        </TableCell>
                    ))}
                    {hasAction && (
                        <TableCell automation-id={getFullTrace(`row-${index}-action`)}>
                            <Skeleton
                                animation="wave"
                                sx={actionSx}
                                variant="circular"
                                width={32}
                                height={32}
                            />
                        </TableCell>
                    )}
                </TableRow>
            ))}
        </>
    );
}
